import { observable, runInAction } from 'mobx'
import type { DomainDto } from '../../dtos/domain'
import type { ErrorKind } from '../error-kind'
import { resolveAccess } from './resolve-access'
import type {
    AccessContext,
    AccessDomainRecord,
    AccessResolution,
    AccessResolved,
    AccessResult,
} from './resolve-access'
import { parseAccessUrl } from './url'
import type { AccessUrl } from './url'

export interface AccessOwnerDeps {
    fetchDomain: (domainId: number) => Promise<DomainDto>
    /** The domain this user had open last, if the browser remembers one for them. */
    readLastUsedDomainId: (userId: number) => number | undefined
    writeLastUsedDomainId: (userId: number, domainId: number) => void
}

/** The background refreshes that can fall behind while the last good data stays on screen. */
export type StaleSource = 'domains' | 'abilities'

export interface StaleEntry {
    source: StaleSource
    kind: ErrorKind
    error: unknown
}

export interface AccessOwner<R extends AccessDomainRecord = AccessDomainRecord> {
    readonly resolution: AccessResolution
    readonly domains: readonly R[]
    /** The open domain; during a switch, the previous one stays readable. */
    readonly domain: DomainDto | undefined
    /**
     * The domain of the last successful resolution. It outlives an unavailable or failed resolution
     * so the app chrome never loses its domain. Its identity changes only on a new resolution or refresh.
     */
    readonly lastResolvedDomain: DomainDto | undefined
    readonly context: AccessContext | undefined
    /**
     * The sources whose latest background refresh failed, or undefined when every source is current.
     * The data they guard is the last good copy, never replaced by an empty one.
     */
    readonly stale: readonly StaleEntry[] | undefined
    /** Records a failed background refresh of a source. A later failure of the same source replaces the entry. */
    markStale: (source: StaleSource, kind: ErrorKind, error: unknown) => void
    /** Clears a source's mark after a successful refresh. */
    clearStale: (source: StaleSource) => void
    /** True while a resolution is in flight. */
    readonly isSwitching: boolean
    /**
     * Tells the owner who is signed in, so "last used domain" is read and written for that user and
     * never for another. Until a user is known no last used domain is read or written, so a bare
     * URL opens the first domain. Naming a different user than the one already set resets the owner
     * first (as `reset` does), so one user's resolution, list and last resolved domain are never
     * served to another. Naming the same user, or the first user, changes nothing else.
     */
    setUser: (userId: number | undefined) => void
    /**
     * Replaces the list of the user's active domains. The list is not the access rule: a domain it does
     * not name is still resolved from the server, which answers 403 or 404 for one the user cannot open,
     * and an open domain the list stops naming stays open because the server already answered for it.
     * A list with the same ids as the old one starts nothing. When the resolution is `unavailable` or
     * `failed` and the changed list now names what the last URL asked for (the user may have just been
     * added), the URL resolves again in the background: the current state stays on screen while it runs,
     * a resolved answer replaces it, and a failure or another refusal leaves it as it was.
     */
    setDomains: (domains: readonly R[]) => void
    /**
     * Forgets everything the previous user had open: the user, the resolution, the domain list and
     * the last resolved domain. An in-flight resolution is dropped.
     */
    reset: () => void
    /**
     * Latest call wins. Resolves to the resolution current when the call settles. A call for the URL
     * key that is already resolved, resolving or unavailable (`domain:<id>`, `org:<id>`, `none`) makes
     * no request and returns that resolution; a failed key is asked again, and `retry` forces a new one.
     *
     * With `keepOnFailure` the call is a switch the URL has not followed yet: when the target is
     * `unavailable` or `failed` the owner goes back to exactly what it held before the call (the open
     * domain stays open, nothing shows the failure) and the returned resolution still carries it.
     * Without it a failure becomes the owner's resolution, as it must when the URL already names the target.
     */
    resolveUrl: (pathname: string, options?: { keepOnFailure?: boolean }) => Promise<AccessResolution>
    /**
     * Resolves the last URL again, even when its key is resolved or resolving. A resolution still in
     * flight is abandoned: its result is dropped when it lands and changes nothing.
     */
    retry: () => Promise<AccessResolution>
    /** Refetches the open domain's detail. Never changes which domain is open. */
    refreshOpenDomain: (domainId: number) => Promise<void>
}

/** The key a URL resolves under: the domain or organization it names, or `none`. */
export const keyOf = (url: AccessUrl): string => {
    switch (url.kind) {
        case 'domain':
            return `domain:${url.domainId}`
        case 'org':
            return `org:${url.orgId}`
        case 'none':
            return 'none'
    }
}

const sameIds = (a: readonly AccessDomainRecord[], b: readonly AccessDomainRecord[]): boolean => {
    if (a.length !== b.length) {
        return false
    }
    const ids = new Set(a.map((d) => d.id))
    return ids.size === new Set(b.map((d) => d.id)).size && b.every((d) => ids.has(d.id))
}

/** True when the list holds what the URL asks for: the domain itself, or any domain for an organization or bare URL. */
const listNames = (domains: readonly AccessDomainRecord[], url: AccessUrl): boolean => {
    switch (url.kind) {
        case 'domain':
            return domains.some((d) => d.id === url.domainId)
        case 'org':
        case 'none':
            // An organization URL resolves through any listed domain, so only an empty list leaves it unavailable.
            return domains.length > 0
    }
}

interface Snapshot {
    resolution: AccessResolution
    latest: Latest | undefined
    lastPathname: string | undefined
}

interface Latest {
    key: string
    /** For a `keepOnFailure` switch: the state a refusal restores. Undefined for a URL-driven resolution. */
    origin: Snapshot | undefined
    /** A URL that named nothing resolved to this domain, so a request for it needs no new resolution. */
    alias: string | undefined
    promise: Promise<AccessResolution>
}

export function createAccessOwner<R extends AccessDomainRecord = AccessDomainRecord>(
    deps: AccessOwnerDeps,
): AccessOwner<R> {
    const state = observable.object(
        {
            resolution: { status: 'idle' } as AccessResolution,
            domains: [] as readonly R[],
            lastResolved: undefined as AccessResolved | undefined,
            stale: undefined as readonly StaleEntry[] | undefined,
        },
        { resolution: observable.ref, domains: observable.ref, lastResolved: observable.ref, stale: observable.ref },
        { deep: false },
    )

    let generation = 0
    let lastPathname: string | undefined
    let latest: Latest | undefined
    let userId: number | undefined
    const refreshes = new Map<number, number>()

    const forget = (): void => {
        generation++
        lastPathname = undefined
        latest = undefined
        runInAction(() => {
            state.resolution = { status: 'idle' }
            state.domains = []
            state.lastResolved = undefined
            state.stale = undefined
        })
    }

    const lastUsedDomainId = (): number | undefined =>
        userId === undefined ? undefined : deps.readLastUsedDomainId(userId)

    const open = (): AccessResolved | undefined => {
        const r = state.resolution
        if (r.status === 'resolved') return r
        if (r.status === 'resolving') return r.previous
        return undefined
    }

    const commit = (resolution: AccessResolution, resolved?: AccessResolved): void => {
        runInAction(() => {
            state.resolution = resolution
            if (resolved) {
                state.lastResolved = resolved
            }
        })
    }

    interface StartOptions {
        keepOnFailure?: boolean
        /** Resolves without committing `resolving`, and leaves the current state alone unless the answer is a resolution. */
        background?: boolean
    }

    const start = async (pathname: string, force: boolean, options: StartOptions = {}): Promise<AccessResolution> => {
        const { keepOnFailure = false, background = false } = options
        const url = parseAccessUrl(pathname)
        const key = keyOf(url)

        if (!force && latest && (latest.key === key || latest.alias === key)) {
            const current = state.resolution
            if (current.status === 'resolved') return current
            if (current.status === 'resolving') return latest.promise
            // The server's refusal is an answer: asking again for the same key repeats it. A failure is not an answer.
            if (current.status === 'unavailable' && latest.key === key) return current
        }

        const mine = ++generation
        const before: Snapshot = { resolution: state.resolution, latest, lastPathname }
        // A switch abandoned by this call must not come back, so a refused switch restores the state
        // before the oldest switch still in flight.
        const origin = before.resolution.status === 'resolving' && before.latest?.origin ? before.latest.origin : before
        lastPathname = pathname

        // A switch that fails leaves the owner as it was, so a failed attempt never shows as the open state.
        const settle = (failure: AccessResult): AccessResolution => {
            if (origin.resolution.status === 'resolving') {
                // A URL-driven resolution was abandoned and nothing would ever settle its generation,
                // so the URL it named resolves again. It is always set: `start` records it before `resolving`.
                lastPathname = origin.lastPathname
                void start(origin.lastPathname as string, true)
                return failure
            }
            lastPathname = origin.lastPathname
            latest = origin.latest
            commit(origin.resolution)
            return failure
        }

        const run = async (): Promise<AccessResolution> => {
            if (!background) {
                commit({ status: 'resolving', previous: state.lastResolved })
            }

            const result = await resolveAccess({
                url,
                domains: state.domains,
                fetchDomain: deps.fetchDomain,
                lastUsedDomainId: lastUsedDomainId(),
            })

            if (mine !== generation) {
                return state.resolution
            }
            if (result.status === 'resolved') {
                if (userId !== undefined) {
                    deps.writeLastUsedDomainId(userId, result.domain.id)
                }
                if (url.kind === 'none' && entry.alias === undefined) {
                    entry.alias = `domain:${result.domain.id}`
                }
                commit(result, result)
                return result
            }

            if (background) {
                return state.resolution
            }

            if (keepOnFailure) {
                return settle(result)
            }

            if (result.status === 'unavailable' && !state.lastResolved) {
                // Nothing has resolved yet, and the app chrome needs a domain to render around the
                // unavailable state: the user's own last used or first domain stands in for it.
                const standIn = await resolveAccess({
                    url: { kind: 'none' },
                    domains: state.domains,
                    fetchDomain: deps.fetchDomain,
                    lastUsedDomainId: lastUsedDomainId(),
                })
                if (mine !== generation) {
                    return state.resolution
                }
                if (standIn.status === 'failed') {
                    commit(standIn)
                    return standIn
                }
                if (standIn.status === 'resolved') {
                    commit(result, { ...standIn, redirectTo: undefined })
                    return result
                }
            }

            commit(result)
            return result
        }

        const entry: Latest = {
            key,
            alias: undefined,
            origin: keepOnFailure ? origin : undefined,
            promise: undefined as unknown as Promise<AccessResolution>,
        }
        latest = entry
        entry.promise = run()
        return entry.promise
    }

    return {
        get resolution() {
            return state.resolution
        },
        get domains() {
            return state.domains
        },
        get domain() {
            return open()?.domain
        },
        get lastResolvedDomain() {
            return state.lastResolved?.domain
        },
        get context() {
            return open()?.context
        },
        get stale() {
            return state.stale
        },
        markStale: (source, kind, error) => {
            runInAction(() => {
                state.stale = [...(state.stale ?? []).filter((e) => e.source !== source), { source, kind, error }]
            })
        },
        clearStale: (source) => {
            runInAction(() => {
                const rest = (state.stale ?? []).filter((e) => e.source !== source)
                state.stale = rest.length > 0 ? rest : undefined
            })
        },
        get isSwitching() {
            return state.resolution.status === 'resolving'
        },
        setUser: (id) => {
            if (userId !== undefined && id !== userId) {
                forget()
            }
            userId = id
        },
        setDomains: (domains) => {
            const previous = state.domains
            runInAction(() => {
                state.domains = domains
            })

            if (lastPathname === undefined || sameIds(previous, domains)) {
                return
            }
            const status = state.resolution.status
            if ((status === 'unavailable' || status === 'failed') && listNames(domains, parseAccessUrl(lastPathname))) {
                void start(lastPathname, true, { background: true })
            }
        },
        reset: () => {
            forget()
            userId = undefined
        },
        resolveUrl: (pathname, options) => start(pathname, false, { keepOnFailure: options?.keepOnFailure === true }),
        retry: async () => (lastPathname === undefined ? state.resolution : start(lastPathname, true)),
        refreshOpenDomain: async (domainId) => {
            const current = state.resolution
            if (current.status !== 'resolved' || current.domain.id !== domainId) {
                return
            }
            // Only the newest refresh of a domain commits, so an older answer that lands later is dropped.
            const mine = (refreshes.get(domainId) ?? 0) + 1
            refreshes.set(domainId, mine)
            const resolvedAt = generation
            const domain = await deps.fetchDomain(domainId)
            const now = state.resolution
            if (
                refreshes.get(domainId) !== mine ||
                resolvedAt !== generation ||
                now.status !== 'resolved' ||
                now.domain.id !== domainId
            ) {
                return
            }
            const next: AccessResolved = { ...now, domain }
            commit(next, next)
        },
    }
}
